import { cardPad } from '../../lib/ui';
import { useApp } from '../../state/AppContext';

type NodeKind = 'sup' | 'dir';
interface NetNodeDef { x: number; y: number; r: number; tag: string; label: string; kind: NodeKind; flagged?: boolean }

export default function Network() {
  const { L } = useApp();
  const NN: Record<string, NetNodeDef> = {
    apex: { x: 150, y: 70, r: 26, tag: 'A', label: 'Apex', kind: 'sup' },
    summit: { x: 90, y: 210, r: 26, tag: 'S', label: 'Summit', kind: 'sup' },
    pacific: { x: 250, y: 250, r: 26, tag: 'P', label: 'Pacific', kind: 'sup' },
    union: { x: 430, y: 90, r: 26, tag: 'U', label: 'ACME Union', kind: 'sup' },
    meridian: { x: 470, y: 240, r: 26, tag: 'M', label: 'Meridian', kind: 'sup' },
    daniel: { x: 175, y: 150, r: 16, tag: 'D', label: 'Daniel C.', kind: 'dir', flagged: true },
    victor: { x: 350, y: 175, r: 16, tag: 'V', label: 'Victor L.', kind: 'dir', flagged: true },
    marcus: { x: 460, y: 165, r: 16, tag: 'R', label: 'Marcus R.', kind: 'dir' },
  };
  const NE: [string, string, boolean][] = [
    ['daniel', 'apex', true],
    ['daniel', 'summit', true],
    ['daniel', 'pacific', false],
    ['victor', 'apex', true],
    ['victor', 'union', false],
    ['marcus', 'meridian', false],
  ];
  const netEdges = NE.map(([a, b, f]) => ({
    x1: NN[a].x, y1: NN[a].y, x2: NN[b].x, y2: NN[b].y,
    stroke: f ? 'var(--color-error)' : 'var(--color-border-strong)',
    w: f ? '2.5' : '1.5',
    dash: f ? '6 4' : '0',
  }));
  const netNodes = Object.values(NN).map((n) => ({
    x: n.x, y: n.y, r: n.r, tag: n.tag, label: n.label, ty: n.y + n.r + 14,
    fill: n.flagged ? 'var(--color-error)' : (n.kind === 'sup' ? 'var(--color-blue)' : 'var(--color-warning)'),
  }));
  const netStats = [
    { label: L('Suppliers', 'ซัพพลายเออร์'), value: '5' },
    { label: L('Shared Directors', 'กรรมการร่วม'), value: '2' },
    { label: L('Flagged Links', 'ลิงก์ที่ถูกแจ้งเตือน'), value: '3' },
    { label: L('Collusion Score', 'คะแนนการสมรู้ร่วมคิด'), value: '72' },
  ];
  const netConnections = [
    { dot: 'var(--color-error)', txt: L('Daniel Coleman directs Apex + Summit (invited to same RFQ)', 'Daniel Coleman เป็นกรรมการของ Apex + Summit (เชิญเข้า RFQ เดียวกัน)') },
    { dot: 'var(--color-error)', txt: L('Victor Lim directs Apex (2nd shared link)', 'Victor Lim เป็นกรรมการของ Apex (ลิงก์ร่วมที่ 2)') },
    { dot: 'var(--color-warning)', txt: L('Marcus Reed — single directorship, no conflict', 'Marcus Reed — เป็นกรรมการรายเดียว ไม่มีความขัดแย้ง') },
  ];

  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 16, marginBottom: 16 }}>
        {netStats.map((s) => (
          <div key={s.label} style={cardPad('16px 18px')}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: 'var(--color-fg-3)', marginBottom: 6 }}>{s.label}</div>
            <div style={{ fontSize: 22, fontWeight: 700, fontFamily: 'var(--font-mono)' }}>{s.value}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: 16, alignItems: 'start' }}>
        <div style={cardPad(12)}>
          <svg viewBox="0 0 560 360" style={{ width: '100%', height: 'auto', display: 'block' }}>
            {netEdges.map((e, i) => (
              <line key={i} x1={e.x1} y1={e.y1} x2={e.x2} y2={e.y2} stroke={e.stroke} strokeWidth={e.w} strokeDasharray={e.dash} />
            ))}
            {netNodes.map((n, i) => (
              <g key={i}>
                <circle cx={n.x} cy={n.y} r={n.r} fill={n.fill} />
                <text x={n.x} y={n.y} textAnchor="middle" dy={4} fill="#fff" fontSize={10} fontWeight={700} fontFamily="var(--font-mono)">{n.tag}</text>
                <text x={n.x} y={n.ty} textAnchor="middle" fill="var(--color-fg-2)" fontSize={11} fontWeight={600}>{n.label}</text>
              </g>
            ))}
          </svg>
          <div style={{ display: 'flex', gap: 18, padding: '10px 8px 4px', fontSize: 11, color: 'var(--color-fg-3)' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}><span style={{ width: 10, height: 10, borderRadius: 999, background: 'var(--color-blue)' }} />{L('Supplier', 'ซัพพลายเออร์')}</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}><span style={{ width: 10, height: 10, borderRadius: 999, background: 'var(--color-warning)' }} />{L('Director', 'กรรมการ')}</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}><span style={{ width: 10, height: 10, borderRadius: 999, background: 'var(--color-error)' }} />{L('Flagged', 'ถูกแจ้งเตือน')}</span>
          </div>
        </div>
        <div style={cardPad(18)}>
          <div style={{ fontWeight: 700, marginBottom: 12 }}>{L('Connection List', 'รายการการเชื่อมโยง')}</div>
          {netConnections.map((c, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--color-border)' }}>
              <span style={{ width: 8, height: 8, borderRadius: 999, background: c.dot, flex: 'none' }} />
              <span style={{ fontSize: 13, fontFamily: 'var(--font-mono)' }}>{c.txt}</span>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
